// Gestão admin de produtos contratados por embarcador (PO1-2154).
// Tela em /app/everlog/produtos-embarcador. Lista embarcadores com chips dos
// produtos vinculados; click abre modal com toggles pra adicionar/remover.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  Row,
  Spinner,
} from "reactstrap";

import api from "../../../services/api";
import { definirVerificado, obterPerfil } from "../../../services/gobidPerfilApi"; // PO1-3529 (épico PO1-3527)
import { useToast } from "../../../hooks/toast";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../components/autopilot/styles";

// Definições locais (label + color) — duplicadas do routes/products.js pra
// evitar import circular (products.js importa o componente desta tela).
// EVERLOG_ADMIN não entra: é sintético, não vinculável via UI.
const ELIGIBLE_PRODUCTS = ["FLOW", "GOBID", "TRACKFY", "AUTOPILOT"];

const PRODUCT_META = {
  FLOW: { label: "Flow", color: "#1d8cf8" },
  GOBID: { label: "GoBid", color: "#00bf9a" },
  TRACKFY: { label: "Trackfy", color: "#ff8d72" },
  AUTOPILOT: { label: "Autopilot", color: "#e14eca" },
};

function ProductChip({ code, onClick }) {
  const meta = PRODUCT_META[code] || { label: code, color: "#888" };
  return (
    <span
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        background: `color-mix(in srgb, ${meta.color} 14%, transparent)`,
        color: meta.color,
        fontSize: "0.74rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {meta.label}
    </span>
  );
}

const EmbarcadoresProdutos = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ativos");
  const [selectedEmbarcador, setSelectedEmbarcador] = useState(null);
  const [saving, setSaving] = useState(false);
  // PO1-3529: selo "Verificado pela Everlog" do perfil GoBID do embarcador.
  // null = ainda carregando / sem resposta; salva a cada clique, como os produtos.
  const [verificado, setVerificado] = useState(null);
  const [savingSelo, setSavingSelo] = useState(false);

  useEffect(() => {
    if (!selectedEmbarcador) {
      setVerificado(null);
      return undefined;
    }
    let ativo = true;
    obterPerfil(selectedEmbarcador.id)
      .then((r) => {
        if (ativo) setVerificado(Boolean(r && r.perfil && r.perfil.verificado));
      })
      .catch(() => {
        if (ativo) setVerificado(false);
      });
    return () => {
      ativo = false;
    };
  }, [selectedEmbarcador]);

  const handleToggleVerificado = async () => {
    if (!selectedEmbarcador || verificado === null) return;
    setSavingSelo(true);
    try {
      const r = await definirVerificado(selectedEmbarcador.id, !verificado);
      setVerificado(Boolean(r && r.verificado));
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: r && r.verificado ? "Selo Verificado ligado" : "Selo Verificado desligado",
      });
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        "Falha ao atualizar o selo.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: msg,
      });
    } finally {
      setSavingSelo(false);
    }
  };

  const load = useCallback(async (status = statusFilter) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/embarcador-features", {
        params: { status },
      });
      setItems(response.data.items || []);
    } catch (err) {
      setError("Não foi possível carregar a lista.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const haystack = [
        it.razaoSocial,
        it.fantasia,
        it.cnpj,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, filter]);

  const handleOpenEdit = (emb) => setSelectedEmbarcador(emb);
  const handleClose = () => setSelectedEmbarcador(null);

  const handleToggleProduct = async (codigo, isActive) => {
    if (!selectedEmbarcador) return;
    setSaving(true);
    try {
      if (isActive) {
        // já está vinculado — remover
        const vinculo = selectedEmbarcador.produtos.find((p) => p.codigo === codigo);
        if (!vinculo) return;
        await api.delete(`/embarcador-features/${vinculo.vinculoId}`);
      } else {
        await api.post("/embarcador-features", {
          embarcadorId: selectedEmbarcador.id,
          featureCodigo: codigo,
        });
      }
      // Refetch a linha atualizada do servidor (atalho: refetch tudo)
      await load();
      // Atualiza o embarcador selecionado com os dados novos
      const updatedRow = (await api.get("/embarcador-features")).data.items.find(
        (it) => it.id === selectedEmbarcador.id,
      );
      if (updatedRow) setSelectedEmbarcador(updatedRow);
      addToast({
        place: "tr",
        type: "success",
        icon: "tim-icons icon-check-2",
        title: isActive ? "Produto removido" : "Produto adicionado",
      });
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        "Falha ao atualizar vínculo.";
      addToast({
        place: "tr",
        type: "danger",
        icon: "tim-icons icon-alert-circle-exc",
        title: msg,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      <Container fluid>
        <Card
          style={{
            ...autopilotCardStyle,
            background: "var(--everlog-card-hero-bg)",
          }}
        >
          <CardHeader style={autopilotHeaderStyle}>
            <div style={autopilotEyebrowStyle}>Everlog Admin</div>
            <div style={{ ...autopilotTitleStyle, marginBottom: "0.25rem" }}>
              Produtos por embarcador
            </div>
            <p style={{ ...autopilotDescriptionStyle, marginBottom: 0 }}>
              Cadastre, troque ou remova os produtos que cada embarcador tem
              acesso. Mudanças refletem na sessão do user empresa após o
              próximo refresh do cache.
            </p>
          </CardHeader>

          <CardBody style={autopilotBodyStyle}>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 14,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1, minWidth: 240 }}>
                <Input
                  type="text"
                  placeholder="Filtrar por nome, fantasia ou CNPJ..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  style={{
                    color: AUTOPILOT_COLORS.title,
                    borderColor: AUTOPILOT_COLORS.border,
                  }}
                />
              </div>
              <div style={{ display: "inline-flex", gap: 6 }}>
                {[
                  { value: "ativos", label: "Ativos" },
                  { value: "todos", label: "Todos" },
                  { value: "inativos", label: "Inativos" },
                ].map((opt) => {
                  const active = statusFilter === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setStatusFilter(opt.value)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 999,
                        border: `1px solid ${
                          active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.borderFaint
                        }`,
                        background: active
                          ? `color-mix(in srgb, ${AUTOPILOT_COLORS.accent} 14%, transparent)`
                          : "transparent",
                        color: active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.text,
                        fontWeight: active ? 700 : 500,
                        fontSize: "0.78rem",
                        cursor: "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {loading ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <Spinner color="info" />
              </div>
            ) : error ? (
              <p style={{ color: AUTOPILOT_COLORS.statusDanger }}>{error}</p>
            ) : filtered.length === 0 ? (
              <p style={{ color: AUTOPILOT_COLORS.muted }}>
                {filter
                  ? "Nenhum embarcador corresponde ao filtro."
                  : "Nenhum embarcador ativo encontrado."}
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
              >
                {filtered.map((emb) => (
                  <div
                    key={emb.id}
                    style={{
                      border: `1px solid ${AUTOPILOT_COLORS.border}`,
                      borderRadius: 12,
                      padding: "12px 16px",
                      background: AUTOPILOT_COLORS.insetSurface,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            color: AUTOPILOT_COLORS.title,
                            fontSize: "0.92rem",
                            fontWeight: 700,
                          }}
                        >
                          {emb.fantasia || emb.razaoSocial}
                        </span>
                        {emb.ativo === false && (
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: 999,
                              background: `color-mix(in srgb, ${AUTOPILOT_COLORS.statusDanger} 14%, transparent)`,
                              color: AUTOPILOT_COLORS.statusDanger,
                              fontSize: "0.66rem",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                            }}
                          >
                            Inativo
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          color: AUTOPILOT_COLORS.muted,
                          fontSize: "0.76rem",
                          marginTop: 2,
                        }}
                      >
                        {emb.razaoSocial}
                        {emb.cnpj ? ` · ${emb.cnpj}` : ""}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {emb.produtos.length === 0 ? (
                        <span
                          style={{
                            color: AUTOPILOT_COLORS.muted,
                            fontSize: "0.78rem",
                            fontStyle: "italic",
                          }}
                        >
                          Sem produtos
                        </span>
                      ) : (
                        emb.produtos.map((p) => (
                          <ProductChip key={p.codigo} code={p.codigo} />
                        ))
                      )}
                      <Button
                        size="sm"
                        style={
                          emb.ativo === false
                            ? {
                                ...autopilotSecondaryButtonStyle,
                                opacity: 0.5,
                                cursor: "not-allowed",
                              }
                            : autopilotSecondaryButtonStyle
                        }
                        onClick={() => handleOpenEdit(emb)}
                        disabled={emb.ativo === false}
                        title={
                          emb.ativo === false
                            ? "Embarcador inativo — reative-o pra alterar produtos"
                            : ""
                        }
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </Container>

      <Modal
        isOpen={Boolean(selectedEmbarcador)}
        toggle={handleClose}
        modalClassName="modal-align-top"
        fade={false}
      >
        <div
          style={{
            padding: "1rem 1.25rem",
            borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
          }}
        >
          <div style={autopilotEyebrowStyle}>Editar produtos</div>
          <div style={{ ...autopilotTitleStyle, marginBottom: "0.15rem" }}>
            {selectedEmbarcador?.fantasia || selectedEmbarcador?.razaoSocial}
          </div>
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem" }}>
            {selectedEmbarcador?.cnpj}
          </div>
        </div>
        <ModalBody>
          <p style={{ ...autopilotDescriptionStyle, marginBottom: "0.85rem" }}>
            Clique nos produtos pra adicionar ou remover. As mudanças são
            salvas imediatamente.
          </p>
          <Row>
            {ELIGIBLE_PRODUCTS.map((codigo) => {
              const meta = PRODUCT_META[codigo];
              const isActive = Boolean(
                selectedEmbarcador?.produtos?.some((p) => p.codigo === codigo),
              );
              return (
                <Col md="6" key={codigo} style={{ marginBottom: 12 }}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handleToggleProduct(codigo, isActive)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "12px 14px",
                      borderRadius: 12,
                      border: `1px solid ${
                        isActive ? meta.color : AUTOPILOT_COLORS.border
                      }`,
                      background: isActive
                        ? `color-mix(in srgb, ${meta.color} 12%, transparent)`
                        : AUTOPILOT_COLORS.insetSurface,
                      color: isActive ? meta.color : AUTOPILOT_COLORS.text,
                      cursor: saving ? "wait" : "pointer",
                      transition: "all 120ms ease",
                    }}
                  >
                    <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>
                      {meta.label}
                    </span>
                    <span
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: isActive ? meta.color : "transparent",
                        border: `1.5px solid ${
                          isActive ? meta.color : AUTOPILOT_COLORS.muted
                        }`,
                        color: isActive ? "#fff" : AUTOPILOT_COLORS.muted,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                      }}
                    >
                      {isActive ? "✓" : ""}
                    </span>
                  </button>
                </Col>
              );
            })}
          </Row>

          {/* PO1-3529 (épico PO1-3527): selo do perfil GoBID, no mesmo molde dos produtos. */}
          <p style={{ ...autopilotDescriptionStyle, margin: "0.5rem 0 0.6rem" }}>
            Selo do perfil GoBID: as transportadoras de fora da carteira veem
            "Verificado pela Everlog" no convite do leilão e do BID.
          </p>
          <button
            type="button"
            disabled={savingSelo || verificado === null}
            onClick={handleToggleVerificado}
            aria-pressed={Boolean(verificado)}
            data-testid="selo-verificado"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: "12px 14px",
              borderRadius: 12,
              border: `1px solid ${verificado ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.border}`,
              background: verificado
                ? AUTOPILOT_COLORS.statusSuccessBg
                : AUTOPILOT_COLORS.insetSurface,
              color: verificado ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.text,
              cursor: savingSelo ? "wait" : "pointer",
              transition: "all 120ms ease",
              marginBottom: 12,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: "0.92rem" }}>
              Verificado pela Everlog
              {verificado === null ? " (carregando...)" : ""}
            </span>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: verificado ? AUTOPILOT_COLORS.statusSuccess : "transparent",
                border: `1.5px solid ${verificado ? AUTOPILOT_COLORS.statusSuccess : AUTOPILOT_COLORS.muted}`,
                color: verificado ? "#fff" : AUTOPILOT_COLORS.muted,
                fontSize: "0.74rem",
                fontWeight: 700,
              }}
            >
              {verificado ? "✓" : ""}
            </span>
          </button>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 6,
            }}
          >
            <Button
              type="button"
              style={{
                background: "transparent",
                borderColor: AUTOPILOT_COLORS.borderFaint,
                color: AUTOPILOT_COLORS.text,
                fontWeight: 600,
              }}
              onClick={handleClose}
            >
              Fechar
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default EmbarcadoresProdutos;
